import { useCallback, useEffect, useMemo, useState } from "react";

import ConsentService from "./consent-service";
import { createConsentService } from "./create-consent-service";
import type { ConsentState } from "./types";

export interface UseConsentsOptions {
  locale: string | null;
  /** Injectable for tests; defaults to {@link createConsentService}. */
  service?: ConsentService;
}

export interface UseConsentsResult {
  consents: ConsentState[];
  isLoading: boolean;
  /** Load failure message for logging; UI should show a generic error instead. */
  error: string | null;
  /** `false` when consents could not be loaded from the CDN. */
  isAvailable: boolean;
  toggle: (id: string) => void;
  /** Mark one consent as interacted with so required errors can show. */
  touch: (id: string) => void;
  /** Mark all consents as interacted with after a blocked submit. */
  touchAll: () => void;
  /** Accepted consent signatures, space-separated for `ulp-consent-signature`. */
  acceptedSignatures: string;
  /** `true` when at least one required consent is still unchecked. */
  hasUnacceptedRequired: boolean;
}

/**
 * Loads consents for a locale and tracks checkbox state (`accepted`, `touched`).
 *
 * Kept outside react-hook-form so SignupForm can read `acceptedSignatures` and
 * `hasUnacceptedRequired` when building the signup payload.
 */
export function useConsents({
  locale,
  service,
}: UseConsentsOptions): UseConsentsResult {
  // Reuse the same service instance unless tests inject a replacement.
  const consentService = useMemo<ConsentService>(
    () => service ?? createConsentService(),
    [service],
  );

  const [consents, setConsents] = useState<ConsentState[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean>(true);

  useEffect(() => {
    if (locale === null) {
      // Unsupported locale or redirect still pending — nothing to load yet.
      setConsents([]);
      setIsLoading(false);
      setError(null);
      setIsAvailable(false);
      return;
    }

    let cancelled = false;

    async function load(consentLocale: string): Promise<void> {
      setIsLoading(true);
      setError(null);

      try {
        const loaded = await consentService.getAllConsents(consentLocale);
        if (cancelled) {
          return;
        }
        // Each consent starts unchecked until the user interacts.
        setConsents(
          loaded.map((consent) => ({
            ...consent,
            accepted: false,
            touched: false,
          })),
        );
        setIsAvailable(true);
      } catch (loadError) {
        if (cancelled) {
          return;
        }
        setError(
          loadError instanceof Error
            ? loadError.message
            : "Could not fetch consents",
        );
        setConsents([]);
        setIsAvailable(false);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load(locale);

    return () => {
      // Locale or service changed — discard the in-flight response.
      cancelled = true;
    };
  }, [locale, consentService]);

  const toggle = useCallback((id: string): void => {
    setConsents((previous) =>
      previous.map((consent) =>
        consent.id === id
          ? { ...consent, accepted: !consent.accepted, touched: true }
          : consent,
      ),
    );
  }, []);

  const touch = useCallback((id: string): void => {
    setConsents((previous) =>
      previous.map((consent) =>
        consent.id === id ? { ...consent, touched: true } : consent,
      ),
    );
  }, []);

  const touchAll = useCallback((): void => {
    setConsents((previous) =>
      previous.map((consent) => ({ ...consent, touched: true })),
    );
  }, []);

  const acceptedSignatures = useMemo(
    // Only accepted consents; space-separated for submit.
    () =>
      consents
        .filter((consent) => consent.accepted)
        .map((consent) => consent.signature)
        .join(" "),
    [consents],
  );

  const hasUnacceptedRequired = useMemo(
    () => consents.some((consent) => consent.required && !consent.accepted),
    [consents],
  );

  return {
    consents,
    isLoading,
    error,
    isAvailable,
    toggle,
    touch,
    touchAll,
    acceptedSignatures,
    hasUnacceptedRequired,
  };
}
